/**
 * Map view — lane-and-row graph rendered with ReactFlow.
 *
 * Three vertical lanes (ingress / canonical / external_sink) with one
 * row per node in each lane. Edges = workflows, coloured by direction.
 *
 * Focus mode: when ``focusedNodeId`` is set, nodes & edges outside the
 * connected subgraph fade to low opacity. Clicking a node toggles its
 * focus (click again to clear). Background click also clears focus.
 *
 * Multi-handle slots prevent fan-in/out from collapsing to a single
 * point — each handle slot is a vertical position on the card edge.
 */
import { Boxes, Inbox, Send } from "lucide-react";
import * as React from "react";
import ReactFlow, {
  Background,
  BackgroundVariant,
  Controls,
  Handle,
  MarkerType,
  Position,
  ReactFlowProvider,
  type Edge,
  type Node,
} from "reactflow";
import "reactflow/dist/style.css";

import { TagList } from "@/components/tags";
import {
  type TopologyEdge,
  type TopologyNode,
  type TopologyNodeType,
} from "@/lib/api-topology";
import { cn } from "@/lib/cn";

import { edgeInFocus, reachableFrom } from "./focus";


// ---------------------------------------------------------------------------
// Layout constants
// ---------------------------------------------------------------------------

const NODE_WIDTH = 220;
const NODE_HEIGHT = 96;
const LANE_X: Record<TopologyNodeType, number> = {
  ingress: 40,
  canonical: 580,
  external_sink: 1120,
};
const ROW_GAP = 150;
const HANDLE_SLOTS = 5;

const TYPE_ICON: Record<TopologyNodeType, typeof Inbox> = {
  ingress: Inbox,
  canonical: Boxes,
  external_sink: Send,
};

// Theme uses HSL channel tokens. ``--fg`` / ``--bg`` (not
// ``--foreground`` / ``--background``) — getting these wrong renders
// edge labels as black-on-black in dark mode.
export const DIRECTION_COLOR: Record<TopologyEdge["direction"], string> = {
  inbound: "hsl(var(--success))",
  internal: "hsl(var(--info))",
  outbound: "hsl(var(--warning))",
};


// ---------------------------------------------------------------------------
// Custom node
// ---------------------------------------------------------------------------

interface NodeData {
  topo: TopologyNode;
  dim: boolean;
  /** Total failures across all edges that touch this node (window). */
  failures: number;
  /** Total runs across all edges that touch this node (window). */
  runs: number;
}

function TopologyNodeCard({ data }: { data: NodeData }) {
  const { topo, dim, failures, runs } = data;
  const Icon = TYPE_ICON[topo.type];
  return (
    <div
      className={cn(
        "group relative flex w-full cursor-pointer flex-col gap-1 overflow-hidden rounded-lg border bg-elevated p-2.5 shadow-sm transition-opacity",
        "hover:border-accent hover:shadow-md",
        topo.enabled ? "border-border" : "border-dashed border-muted-foreground/40",
        failures > 0 && "ring-1 ring-danger/40",
        dim ? "opacity-25" : "opacity-100",
      )}
      style={{ width: NODE_WIDTH, height: NODE_HEIGHT }}
    >
      {failures > 0 && (
        <span
          className="absolute right-1.5 top-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 font-mono text-[10px] font-medium text-white"
          title={`${failures} failed run(s) in last 24h`}
        >
          {failures}
        </span>
      )}
      {runs > 0 && failures === 0 && (
        <span
          className="absolute right-1.5 top-1.5 inline-flex h-1.5 w-1.5 rounded-full bg-success"
          title={`${runs} successful run(s) in last 24h`}
        />
      )}
      {Array.from({ length: HANDLE_SLOTS }).map((_, i) => {
        const top = `${10 + (i * 80) / (HANDLE_SLOTS - 1)}%`;
        return (
          <React.Fragment key={i}>
            <Handle
              id={`tgt-${i}`}
              type="target"
              position={Position.Left}
              style={{ top, background: "transparent", border: "none", width: 6, height: 6, left: -2 }}
              isConnectable={false}
            />
            <Handle
              id={`src-${i}`}
              type="source"
              position={Position.Right}
              style={{ top, background: "transparent", border: "none", width: 6, height: 6, right: -2 }}
              isConnectable={false}
            />
          </React.Fragment>
        );
      })}
      <div className="flex items-center gap-2">
        <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" strokeWidth={1.5} />
        <span className="truncate text-sm font-medium">{topo.label}</span>
      </div>
      <div className="truncate text-[11px] text-muted-foreground">{topo.sublabel}</div>
      {topo.tags.length > 0 && <TagList tags={topo.tags} max={2} />}
    </div>
  );
}

const NODE_TYPES = { topo: TopologyNodeCard };


// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

function aggregateNodeMetrics(
  edges: TopologyEdge[],
): Map<string, { runs: number; failures: number }> {
  // For each node, sum the runs+failures of every edge that touches
  // it. A single workflow's metrics is counted once per node it
  // connects to (which is fine — it's a node-centric view).
  const out = new Map<string, { runs: number; failures: number }>();
  const bump = (id: string, runs: number, fails: number) => {
    const cur = out.get(id) ?? { runs: 0, failures: 0 };
    cur.runs += runs;
    cur.failures += fails;
    out.set(id, cur);
  };
  edges.forEach((e) => {
    bump(e.source, e.metrics.runs_total, e.metrics.runs_failed);
    bump(e.target, e.metrics.runs_total, e.metrics.runs_failed);
  });
  return out;
}

function layoutNodes(
  nodes: TopologyNode[],
  edges: TopologyEdge[],
  focus: Set<string> | null,
): Node<NodeData>[] {
  const nodeMetrics = aggregateNodeMetrics(edges);
  const byType: Record<TopologyNodeType, TopologyNode[]> = {
    ingress: [],
    canonical: [],
    external_sink: [],
  };
  nodes.forEach((n) => byType[n.type].push(n));

  byType.canonical.sort((a, b) => a.label.localeCompare(b.label));
  byType.ingress.sort((a, b) => {
    const cA = edges.find((e) => e.source === a.id)?.target ?? "";
    const cB = edges.find((e) => e.source === b.id)?.target ?? "";
    return cA.localeCompare(cB) || a.label.localeCompare(b.label);
  });
  byType.external_sink.sort((a, b) => {
    const cA = edges.find((e) => e.target === a.id)?.source ?? "";
    const cB = edges.find((e) => e.target === b.id)?.source ?? "";
    return cA.localeCompare(cB) || a.label.localeCompare(b.label);
  });

  const out: Node<NodeData>[] = [];
  (Object.keys(byType) as TopologyNodeType[]).forEach((type) => {
    byType[type].forEach((n, i) => {
      const dim = focus !== null && !focus.has(n.id);
      const stats = nodeMetrics.get(n.id) ?? { runs: 0, failures: 0 };
      out.push({
        id: n.id,
        type: "topo",
        position: { x: LANE_X[type], y: 40 + i * ROW_GAP },
        data: { topo: n, dim, runs: stats.runs, failures: stats.failures },
        draggable: false,
        connectable: false,
        selectable: true,
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
        style: {
          width: NODE_WIDTH,
          height: NODE_HEIGHT,
          background: "transparent",
          border: "none",
          padding: 0,
        },
      });
    });
  });
  return out;
}


// ---------------------------------------------------------------------------
// Edge building (with multi-handle fan-out + focus dimming)
// ---------------------------------------------------------------------------

function pickHandle(rank: number, count: number): number {
  if (count <= 1) return Math.floor((HANDLE_SLOTS - 1) / 2);
  const ratio = rank / (count - 1);
  return Math.round(ratio * (HANDLE_SLOTS - 1));
}

function buildEdges(
  edges: TopologyEdge[],
  rfNodes: Node<NodeData>[],
  focus: Set<string> | null,
): Edge[] {
  const yById = new Map<string, number>();
  rfNodes.forEach((n) => yById.set(n.id, n.position.y));

  const sourceBuckets = new Map<string, TopologyEdge[]>();
  const targetBuckets = new Map<string, TopologyEdge[]>();
  edges.forEach((e) => {
    (sourceBuckets.get(e.source) ?? sourceBuckets.set(e.source, []).get(e.source)!).push(e);
    (targetBuckets.get(e.target) ?? targetBuckets.set(e.target, []).get(e.target)!).push(e);
  });
  for (const list of sourceBuckets.values()) {
    list.sort((a, b) => (yById.get(a.target) ?? 0) - (yById.get(b.target) ?? 0));
  }
  for (const list of targetBuckets.values()) {
    list.sort((a, b) => (yById.get(a.source) ?? 0) - (yById.get(b.source) ?? 0));
  }
  const sourceRank = new Map<string, number>();
  const targetRank = new Map<string, number>();
  sourceBuckets.forEach((list) => list.forEach((e, i) => sourceRank.set(e.id, i)));
  targetBuckets.forEach((list) => list.forEach((e, i) => targetRank.set(e.id, i)));

  return edges.map((e) => {
    const directionColor = DIRECTION_COLOR[e.direction] ?? "#888";
    const inFocus = edgeInFocus(e, focus);
    const srcSlot = pickHandle(
      sourceRank.get(e.id) ?? 0,
      sourceBuckets.get(e.source)?.length ?? 1,
    );
    const tgtSlot = pickHandle(
      targetRank.get(e.id) ?? 0,
      targetBuckets.get(e.target)?.length ?? 1,
    );

    // Runtime-aware edge styling. The base colour stays the direction
    // colour (so direction is still legible at a glance) but we
    // overlay:
    //   - red wash when the last run failed
    //   - dotted line when there's been no traffic in the window
    //   - thicker stroke proportional to volume (log scale)
    const m = e.metrics;
    const hasFailure = m.last_status === "failed";
    const isIdle = m.runs_total === 0;
    const color = hasFailure ? "hsl(var(--danger))" : directionColor;
    const baseWidth = e.enabled ? 1.5 : 1;
    const volumeBoost = m.runs_total > 0 ? Math.min(2.5, Math.log10(m.runs_total + 1) * 1.2) : 0;
    const strokeWidth = baseWidth + volumeBoost;
    const dash = !e.enabled ? "4 3" : isIdle ? "2 4" : undefined;

    return {
      id: e.id,
      source: e.source,
      target: e.target,
      sourceHandle: `src-${srcSlot}`,
      targetHandle: `tgt-${tgtSlot}`,
      // Default = bezier. Curves naturally separate when N edges
      // converge on the same sink, where smoothstep collapsed them
      // onto a single vertical segment.
      type: "default",
      animated: m.runs_running > 0,
      label: e.workflow_name,
      labelStyle: {
        fontSize: 11,
        fontWeight: 500,
        fill: "hsl(var(--fg))",
        fontFamily: "ui-monospace, SFMono-Regular, monospace",
      },
      labelBgStyle: {
        fill: "hsl(var(--elevated))",
        fillOpacity: 1,
        stroke: color,
        strokeOpacity: inFocus ? 0.6 : 0.15,
        strokeWidth: 1,
      },
      labelBgPadding: [8, 4],
      labelBgBorderRadius: 6,
      style: {
        stroke: color,
        strokeWidth,
        strokeDasharray: dash,
        opacity: inFocus ? 1 : 0.15,
      },
      markerEnd: { type: MarkerType.ArrowClosed, color, width: 16, height: 16 },
    };
  });
}


// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface MapViewProps {
  nodes: TopologyNode[];
  edges: TopologyEdge[];
  focusedNodeId: string | null;
  onFocus: (nodeId: string | null) => void;
  onOpenNode: (node: TopologyNode) => void;
}

export function MapView({
  nodes,
  edges,
  focusedNodeId,
  onFocus,
  onOpenNode,
}: MapViewProps) {
  const focus = React.useMemo(
    () => reachableFrom(focusedNodeId, edges),
    [focusedNodeId, edges],
  );

  const rfNodes = React.useMemo(
    () => layoutNodes(nodes, edges, focus),
    [nodes, edges, focus],
  );
  const rfEdges = React.useMemo(
    () => buildEdges(edges, rfNodes, focus),
    [edges, rfNodes, focus],
  );

  return (
    <ReactFlowProvider>
      <ReactFlow
        nodes={rfNodes}
        edges={rfEdges}
        nodeTypes={NODE_TYPES}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        proOptions={{ hideAttribution: true }}
        nodesDraggable={false}
        nodesConnectable={false}
        panOnScroll
        zoomOnScroll={false}
        minZoom={0.4}
        maxZoom={1.5}
        onNodeClick={(_, node) => {
          const topo = (node.data as NodeData).topo;
          // Click on the already-focused node clears focus; otherwise
          // it becomes the new focal point. Either way the drawer
          // opens on the clicked node so the user sees its workflows.
          if (focusedNodeId === topo.id) {
            onFocus(null);
          } else {
            onFocus(topo.id);
          }
          onOpenNode(topo);
        }}
        onPaneClick={() => onFocus(null)}
      >
        <Background variant={BackgroundVariant.Dots} gap={20} size={1} />
        <Controls showInteractive={false} />
      </ReactFlow>
    </ReactFlowProvider>
  );
}
